import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddLocationIcon from '@mui/icons-material/AddLocation';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { useAuth } from '../../../../contexts/AuthContext';
import phoneCallService, { 
  CreateShippingAddressDto, 
  ShippingAddressDto 
} from '../../../../services/phoneCallService';

// ============================================================================
// INTERFACES
// ============================================================================

interface AddShippingAddressModalProps {
  open: boolean;
  onClose: () => void;
  accountId: number;
  customerName: string;
  customerPhone: string;
  onAddressAdded?: (address: ShippingAddressDto) => void;
}

// Türkiye illeri
const TURKEY_CITIES = [
  'Adana', 'Adıyaman', 'Afyonkarahisar', 'Ağrı', 'Aksaray', 'Amasya', 'Ankara', 'Antalya', 
  'Ardahan', 'Artvin', 'Aydın', 'Balıkesir', 'Bartın', 'Batman', 'Bayburt', 'Bilecik', 
  'Bingöl', 'Bitlis', 'Bolu', 'Burdur', 'Bursa', 'Çanakkale', 'Çankırı', 'Çorum', 
  'Denizli', 'Diyarbakır', 'Düzce', 'Edirne', 'Elazığ', 'Erzincan', 'Erzurum', 'Eskişehir', 
  'Gaziantep', 'Giresun', 'Gümüşhane', 'Hakkari', 'Hatay', 'Iğdır', 'Isparta', 'İstanbul', 
  'İzmir', 'Kahramanmaraş', 'Karabük', 'Karaman', 'Kars', 'Kastamonu', 'Kayseri', 'Kırıkkale', 
  'Kırklareli', 'Kırşehir', 'Kilis', 'Kocaeli', 'Konya', 'Kütahya', 'Malatya', 'Manisa', 
  'Mardin', 'Mersin', 'Muğla', 'Muş', 'Nevşehir', 'Niğde', 'Ordu', 'Osmaniye', 
  'Rize', 'Sakarya', 'Samsun', 'Şanlıurfa', 'Siirt', 'Sinop', 'Şırnak', 'Sivas', 
  'Tekirdağ', 'Tokat', 'Trabzon', 'Tunceli', 'Uşak', 'Van', 'Yalova', 'Yozgat', 'Zonguldak'
];

// Adres etiketleri
const ADDRESS_LABELS = ['Ev', 'İş', 'Yazlık', 'Diğer'];

// ============================================================================
// COMPONENT
// ============================================================================

const AddShippingAddressModal: React.FC<AddShippingAddressModalProps> = ({
  open,
  onClose,
  accountId,
  customerName,
  customerPhone,
  onAddressAdded,
}) => {
  const { colors: themeColors } = useThemeContext();
  const { userDetails } = useAuth();
  const companyId = userDetails?.companyId;

  // Form state
  const [addressLabel, setAddressLabel] = useState('Ev');
  const [city, setCity] = useState('');
  const [district, setDistrict] = useState('');
  const [address, setAddress] = useState('');
  const [authorizedPerson, setAuthorizedPerson] = useState('');
  const [phone, setPhone] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [mahalle, setMahalle] = useState('');
  const [cadde, setCadde] = useState('');
  const [sokak, setSokak] = useState('');
  const [apartman, setApartman] = useState('');
  const [siteAdi, setSiteAdi] = useState('');
  const [blok, setBlok] = useState('');
  const [binaNo, setBinaNo] = useState('');
  const [kat, setKat] = useState('');
  const [daireNo, setDaireNo] = useState('');

  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Modal açıldığında varsayılan değerleri set et
  useEffect(() => {
    if (open) {
      setAuthorizedPerson(customerName);
      setPhone(customerPhone);
    }
  }, [open, customerName, customerPhone]);

  // Modal kapandığında formu temizle
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  // ============================================================================
  // FUNCTIONS
  // ============================================================================

  const resetForm = () => {
    setAddressLabel('Ev');
    setCity('');
    setDistrict('');
    setAddress('');
    setAuthorizedPerson('');
    setPhone('');
    setDeliveryNotes('');
    setIsDefault(false);
    setMahalle('');
    setCadde('');
    setSokak('');
    setApartman('');
    setSiteAdi('');
    setBlok('');
    setBinaNo('');
    setKat('');
    setDaireNo('');
    setError(null);
    setSuccess(false);
  };

  const validateForm = (): boolean => {
    const hasStructuredAddress = mahalle.trim() || cadde.trim() || sokak.trim() || binaNo.trim();
    if (!address.trim() && !hasStructuredAddress) {
      setError('Açık adres veya detaylı adres alanlarından en az biri doldurulmalıdır');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!companyId) {
      setError('Şirket bilgisi bulunamadı');
      return;
    }

    if (!validateForm()) return;

    setSaving(true);
    setError(null);

    try {
      const data: CreateShippingAddressDto = {
        AccountId: accountId,
        AddressLabel: addressLabel,
        Country: 'Türkiye',
        City: city,
        District: district.trim(),
        Address: address.trim(),
        AuthorizedPerson: authorizedPerson.trim() || customerName,
        Phone: phone.trim() || customerPhone,
        IsDefault: isDefault,
        DeliveryNotes: deliveryNotes.trim() || undefined,
        Mahalle: mahalle.trim() || undefined,
        Cadde: cadde.trim() || undefined,
        Sokak: sokak.trim() || undefined,
        Apartman: apartman.trim() || undefined,
        SiteAdi: siteAdi.trim() || undefined,
        Blok: blok.trim() || undefined,
        BinaNo: binaNo.trim() || undefined,
        Kat: kat.trim() || undefined,
        DaireNo: daireNo.trim() || undefined,
      };

      console.log('📍 [Address] Yeni adres ekleniyor:', data);
      
      const result = await phoneCallService.addShippingAddress(data);
      
      console.log('✅ [Address] Adres eklendi:', result);
      
      setSuccess(true);
      
      // Callback ile parent'a bildir
      if (onAddressAdded) {
        onAddressAdded(result);
      }

      // 1 saniye sonra kapat
      setTimeout(() => {
        onClose();
      }, 1000);

    } catch (err: any) {
      console.error('❌ [Address] Adres ekleme hatası:', err);
      
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Adres eklenemedi. Lütfen tekrar deneyin.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          bgcolor: themeColors.primary,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddLocationIcon />
          <Typography variant="h6">Yeni Adres Ekle</Typography>
        </Box>
        <IconButton onClick={handleClose} disabled={saving} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {/* Müşteri Bilgisi */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{customerName}</strong> müşterisine yeni adres ekleniyor
          </Typography>
        </Alert>

        {/* Hata Mesajı */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Başarı Mesajı */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            ✅ Adres başarıyla eklendi!
          </Alert>
        )}

        {/* Form */}
        <Grid container spacing={2}>
          {/* ADRES BİLGİLERİ */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LocationOnIcon sx={{ color: themeColors.primary }} />
              <Typography variant="subtitle1" fontWeight="bold">
                Adres Bilgileri
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Adres Etiketi</InputLabel>
              <Select
                value={addressLabel}
                onChange={(e) => setAddressLabel(e.target.value)}
                label="Adres Etiketi"
                disabled={saving}
              >
                {ADDRESS_LABELS.map((label) => (
                  <MenuItem key={label} value={label}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  disabled={saving}
                  sx={{
                    color: themeColors.primary,
                    '&.Mui-checked': { color: themeColors.primary },
                  }}
                />
              }
              label="Varsayılan adres olarak ayarla"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>İl</InputLabel>
              <Select
                value={city}
                onChange={(e) => setCity(e.target.value)}
                label="İl"
                disabled={saving}
              >
                <MenuItem value=""><em>Seçilmedi</em></MenuItem>
                {TURKEY_CITIES.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="İlçe"
              value={district}
              onChange={(e) => setDistrict(e.target.value)}
              placeholder="İlçe adı"
              disabled={saving}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Mahalle" value={mahalle} onChange={(e) => setMahalle(e.target.value)} placeholder="Mahalle adı" disabled={saving} size="small" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Cadde" value={cadde} onChange={(e) => setCadde(e.target.value)} placeholder="Cadde adı" disabled={saving} size="small" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Sokak" value={sokak} onChange={(e) => setSokak(e.target.value)} placeholder="Sokak adı" disabled={saving} size="small" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Apartman / Bina Adı" value={apartman} onChange={(e) => setApartman(e.target.value)} placeholder="Apartman adı" disabled={saving} size="small" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Site Adı" value={siteAdi} onChange={(e) => setSiteAdi(e.target.value)} placeholder="Site / Toplu konut adı" disabled={saving} size="small" />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth label="Blok" value={blok} onChange={(e) => setBlok(e.target.value)} placeholder="Blok" disabled={saving} size="small" />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth label="Bina No" value={binaNo} onChange={(e) => setBinaNo(e.target.value)} placeholder="No" disabled={saving} size="small" />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth label="Kat" value={kat} onChange={(e) => setKat(e.target.value)} placeholder="Kat" disabled={saving} size="small" />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth label="Daire No" value={daireNo} onChange={(e) => setDaireNo(e.target.value)} placeholder="Daire" disabled={saving} size="small" />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Açık Adres"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Serbest metin adres (opsiyonel - yukarıdaki alanlar doldurulmuşsa otomatik oluşturulur)"
              disabled={saving}
              size="small"
              multiline
              rows={2}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Yetkili Kişi"
              value={authorizedPerson}
              onChange={(e) => setAuthorizedPerson(e.target.value)}
              placeholder="Ad Soyad"
              disabled={saving}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Telefon"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="05XX XXX XXXX"
              disabled={saving}
              size="small"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Teslimat Notu (Opsiyonel)"
              value={deliveryNotes}
              onChange={(e) => setDeliveryNotes(e.target.value)}
              placeholder="Kapıda bırakılabilir, zil çalışmıyor vb."
              disabled={saving}
              size="small"
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={saving}
          color="inherit"
        >
          İptal
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || success}
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <AddLocationIcon />}
          sx={{
            bgcolor: themeColors.primary,
            '&:hover': { bgcolor: themeColors.primary, filter: 'brightness(0.9)' },
          }}
        >
          {saving ? 'Kaydediliyor...' : 'Adresi Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddShippingAddressModal;
